/**
 * ToastProvider component
 * Provides toast notification context and rendering
 */

import { createContext, useContext, useState, type ReactNode } from 'react';
import { Toast, type ToastVariant, type ToastProps } from './Toast';

interface ToastConfig {
  variant: ToastVariant;
  message: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  duration?: number;
}

interface ToastContextValue {
  showToast: (config: ToastConfig) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  warning: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

interface ToastProviderProps {
  children: ReactNode;
}

/**
 * Toast provider component
 * Wrap your app with this to enable toast notifications
 *
 * @example
 * ```tsx
 * <ToastProvider>
 *   <App />
 * </ToastProvider>
 * ```
 */
export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<Array<Omit<ToastProps, 'onClose'>>>([]);

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  const showToast = ({ variant, message, action, duration = 5000 }: ToastConfig) => {
    const id = Date.now().toString();
    setToasts((prev) => [...prev, { id, variant, message, action, duration }]);
  };

  const success = (message: string) => showToast({ variant: 'success', message });
  const error = (message: string) => showToast({ variant: 'error', message });
  const warning = (message: string) => showToast({ variant: 'warning', message });
  const info = (message: string) => showToast({ variant: 'info', message });

  return (
    <ToastContext.Provider value={{ showToast, success, error, warning, info }}>
      {children}
      {/* Toast container */}
      <div
        className="fixed bottom-0 right-0 z-50 p-4 space-y-2 pointer-events-none"
        aria-live="polite"
        aria-atomic="true"
      >
        {toasts.map((toast) => (
          <div key={toast.id} className="pointer-events-auto">
            <Toast {...toast} onClose={removeToast} />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/**
 * Custom hook to access toast notifications
 *
 * @example
 * ```tsx
 * const toast = useToast();
 * toast.success('Saved!');
 * toast.error('Failed to save');
 * ```
 */
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
}
